import { benefitCentsOf } from "@/lib/discount-evaluation/allocations"
import { buildEvaluationLines } from "@/lib/discount-evaluation/cart-lines"
import type {
  CartLinePayload,
  CouponEvaluation,
  EvaluateCouponsRequest,
  RulePromotionEvaluation,
} from "@/lib/discount-evaluation/types"
import type { Cart, Category } from "@/types"
import { atom } from "jotai"
import type { CheckoutState } from "../state"
import { deriveCheckoutPricing, type CheckoutPricing } from "./checkout-pricing"

/** The last server answer for one exact request, identified by `key`. */
export type EvaluationSnapshot<T> =
  | { status: "idle" }
  | { status: "loading"; key: string }
  | { status: "ready"; key: string; data: T }
  | { status: "error"; key: string }

/** Server state, never persisted: a reload asks again rather than trusting an old answer. */
export const rulePromotionSnapshotAtom = atom<EvaluationSnapshot<RulePromotionEvaluation>>({ status: "idle" })

/** Server state, never persisted: a reload asks again rather than trusting an old answer. */
export const couponSnapshotAtom = atom<EvaluationSnapshot<CouponEvaluation>>({ status: "idle" })

/** Bumped by "Tentar de novo" so a failed evaluation is asked again for the same bag. */
export const discountRetryAtom = atom(0)

/** Where an evaluation stands for the current bag. `none` means there is nothing to ask. */
export type EvaluationPhase = "none" | "checking" | "ready" | "error"

export type RulePromotionRequest = { lines: CartLinePayload[]; deliveryFeeCents: number }

export type DiscountPlan = {
  pricing: CheckoutPricing
  rulePromotions: {
    key: string
    request: RulePromotionRequest | null
    phase: EvaluationPhase
    data: RulePromotionEvaluation | null
  }
  coupons: {
    key: string
    request: EvaluateCouponsRequest | null
    phase: EvaluationPhase
    data: CouponEvaluation | null
  }
}

function freshData<T>(snapshot: EvaluationSnapshot<T>, key: string): T | null {
  return snapshot.status === "ready" && snapshot.key === key ? snapshot.data : null
}

function phaseOf<T>(snapshot: EvaluationSnapshot<T>, key: string, wanted: boolean): EvaluationPhase {
  if (!wanted) return "none"
  if (snapshot.status === "ready" && snapshot.key === key) return "ready"
  if (snapshot.status === "error" && snapshot.key === key) return "error"
  return "checking"
}

/**
 * What to ask the server for this exact bag, and the pricing built from the
 * answers that match it. Loyalty goes first: the rule promotions it returns,
 * the rewards picked and the benefits already claimed are allocated per line,
 * and only then are the coupons evaluated on top of them (contract C1.1).
 * An answer for any other bag is ignored, so a total never mixes a fresh
 * cart with a stale discount. `evaluate: false` (table orders) asks nothing.
 */
export function planDiscounts(
  state: CheckoutState,
  cart: Cart,
  categories: Category[],
  snapshots: {
    rulePromotions: EvaluationSnapshot<RulePromotionEvaluation>
    coupons: EvaluationSnapshot<CouponEvaluation>
  },
  options: { evaluate: boolean } = { evaluate: true },
): DiscountPlan {
  const deliveryFeeCents = Math.round(state.servicePrice * 100)
  const wantsRulePromotions = options.evaluate && cart.items.length > 0
  const ruleRequest = wantsRulePromotions
    ? { lines: buildEvaluationLines(cart, categories), deliveryFeeCents }
    : null
  const ruleKey = ruleRequest ? JSON.stringify(ruleRequest) : ""
  const rulePromotions = freshData(snapshots.rulePromotions, ruleKey)
  const rulePhase = phaseOf(snapshots.rulePromotions, ruleKey, wantsRulePromotions)

  const benefitsSettled = rulePhase !== "checking"
  const wantsCoupons = options.evaluate && state.couponCodes.length > 0 && cart.items.length > 0
  let couponRequest: EvaluateCouponsRequest | null = null
  if (wantsCoupons && benefitsSettled) {
    const benefits = deriveCheckoutPricing(state, cart, { rulePromotions }).benefitDiscounts
    const cents = benefitCentsOf(benefits, cart)
    couponRequest = {
      couponCodes: state.couponCodes,
      lines: buildEvaluationLines(cart, categories, cents.lines),
      deliveryFeeCents,
      cartBenefitDiscountCents: cents.cart,
      deliveryBenefitDiscountCents: cents.delivery,
    }
  }
  const couponKey = couponRequest ? JSON.stringify(couponRequest) : ""
  const coupons = couponRequest ? freshData(snapshots.coupons, couponKey) : null
  const couponPhase = wantsCoupons
    ? couponRequest
      ? phaseOf(snapshots.coupons, couponKey, true)
      : "checking"
    : "none"

  return {
    pricing: deriveCheckoutPricing(state, cart, { rulePromotions, coupons }),
    rulePromotions: { key: ruleKey, request: ruleRequest, phase: rulePhase, data: rulePromotions },
    coupons: { key: couponKey, request: couponRequest, phase: couponPhase, data: coupons },
  }
}
